import { getAttributesWithoutPrefix } from 'tradera/features/discover/search';
import { getPriceRange } from './price-range';

import type {
    CampaignPageApiRequest,
    CampaignPageApiResponse,
    CategoryPageApiRequest,
    CategoryPageApiResponse,
    CharityPageApiRequest,
    CharityPageApiResponse,
    IndependentSearchApiRequest,
    IndependentSearchApiResponse,
    SearchPageApiRequest,
    SearchPageApiResponse,
    ShopPageApiRequest,
    ShopPageApiResponse,
    ProfileItemsApiRequest,
    ProfileItemsApiResponse,
    CausePageApiRequest,
    CausePageApiResponse
} from 'tradera/state/services/types/webapi-discover-generated';

type FixedIndependentSearchApiRequest = Omit<
    IndependentSearchApiRequest,
    'automaticTranslationPreferred'
> & {
    automaticTranslationPreferred: boolean;
};

// TODO: Temporary solution to use correct required properties in RequestType. ProfileItemsApi has nullable reference in webapi.
export type CommonSearchRequestType = Pick<
    ProfileItemsApiRequest,
    | 'languageCodeIso2'
    | 'shippingCountryCodeIso2'
    | 'automaticTranslationPreferred'
> &
    Omit<
        {
            [P in keyof SearchPageApiRequest &
                keyof CategoryPageApiRequest &
                keyof CampaignPageApiRequest &
                keyof CharityPageApiRequest &
                keyof ShopPageApiRequest &
                keyof FixedIndependentSearchApiRequest &
                keyof ProfileItemsApiRequest &
                keyof CausePageApiRequest]:
                | SearchPageApiRequest[P]
                | CategoryPageApiRequest[P]
                | CampaignPageApiRequest[P]
                | CharityPageApiRequest[P]
                | ShopPageApiRequest[P]
                | FixedIndependentSearchApiRequest[P]
                | ProfileItemsApiRequest[P]
                | CausePageApiRequest[P];
        },
        | 'languageCodeIso2'
        | 'shippingCountryCodeIso2'
        | 'automaticTranslationPreferred'
    >;

export type CommonSearchResponseType = {
    [P in keyof SearchPageApiResponse &
        keyof CategoryPageApiResponse &
        keyof CampaignPageApiResponse &
        keyof CharityPageApiResponse &
        keyof ShopPageApiResponse &
        keyof IndependentSearchApiResponse &
        keyof ProfileItemsApiResponse &
        keyof CausePageApiResponse]:
        | SearchPageApiResponse[P]
        | CategoryPageApiResponse[P]
        | CampaignPageApiResponse[P]
        | CharityPageApiResponse[P]
        | ShopPageApiResponse[P]
        | IndependentSearchApiResponse[P]
        | ProfileItemsApiResponse[P]
        | CausePageApiResponse[P];
};

type RemoveUndefined<T> = {
    [K in keyof T]: Exclude<T[K], undefined>;
};

export const createIndependentSearchApiRequest = (
    countryCodeIso2: string,
    automaticTranslationPreferred: boolean,
    languageCodeIso2: string,
    searchParams: { [key: string]: string }
): Required<IndependentSearchApiRequest> => {
    const request = createCommonSearchApiRequest(
        countryCodeIso2,
        automaticTranslationPreferred,
        languageCodeIso2,
        searchParams
    );
    return {
        ...request,
        alias: searchParams.alias,
        query: searchParams.q,
        queryScope: searchParams.queryScope,
        searchInLongDescription: searchParams.searchInLongDescription
            ? searchParams.searchInLongDescription === 'true'
            : null,
        searchTypeVariantHint: searchParams.searchTypeVariantHint,
        sellerType: searchParams.sellerType,
        county: searchParams.county,
        zipCode: searchParams.zipCode || searchParams.zipcode
    };
};

export const createCategoryPageApiRequest = (
    countryCodeIso2: string,
    automaticTranslationPreferred: boolean,
    languageCodeIso2: string,
    searchParams: { [key: string]: string }
): Required<CategoryPageApiRequest> => {
    const request = createCommonSearchApiRequest(
        countryCodeIso2,
        automaticTranslationPreferred,
        languageCodeIso2,
        searchParams
    );
    return {
        ...request,
        alias: searchParams.alias,
        allowedBuyerRegions: searchParams.allowedBuyerRegions,
        sellerType: searchParams.sellerType,
        county: searchParams.county,
        zipCode: searchParams.zipCode || searchParams.zipcode,
        shippingTypes:
            searchParams.shippingTypes !== undefined
                ? decodeURIComponent(searchParams.shippingTypes).split(';')
                : null
    };
};

export const createProfileItemsApiRequest = (
    memberId: number,
    countryCodeIso2: string,
    automaticTranslationPreferred: boolean,
    languageCodeIso2: string,
    searchParams: { [key: string]: string }
): ProfileItemsApiRequest => {
    const request = createCommonSearchApiRequest(
        countryCodeIso2,
        automaticTranslationPreferred,
        languageCodeIso2,
        searchParams
    );
    return {
        ...request,
        memberId: memberId,
        query: searchParams.q,
        queryScope: searchParams.queryScope,
        searchInLongDescription: searchParams.searchInLongDescription
            ? searchParams.searchInLongDescription === 'true'
            : null,
        shippingTypes:
            searchParams.shippingTypes !== undefined
                ? decodeURIComponent(searchParams.shippingTypes).split(';')
                : null,
        allowedBuyerRegions: searchParams.allowedBuyerRegions
    };
};

export const createSearchPageApiRequest = (
    countryCodeIso2: string,
    automaticTranslationPreferred: boolean,
    languageCodeIso2: string,
    searchParams: { [key: string]: string }
): Required<SearchPageApiRequest> => {
    const request = createCommonSearchApiRequest(
        countryCodeIso2,
        automaticTranslationPreferred,
        languageCodeIso2,
        searchParams
    );
    return {
        ...request,
        alias: searchParams.alias,
        query: searchParams.q,
        queryScope: searchParams.queryScope,
        searchInLongDescription: searchParams.searchInLongDescription
            ? searchParams.searchInLongDescription === 'true'
            : null,
        searchTypeVariantHint: searchParams.searchTypeVariantHint,
        sellerType: searchParams.sellerType,
        county: searchParams.county,
        zipCode: searchParams.zipCode || searchParams.zipcode,
        shippingTypes:
            searchParams.shippingTypes !== undefined
                ? decodeURIComponent(searchParams.shippingTypes).split(';')
                : null,
        allowedBuyerRegions: searchParams.allowedBuyerRegions
    };
};

export const createCommonSearchApiRequest = (
    countryCodeIso2: string,
    automaticTranslationPreferred: boolean,
    languageCodeIso2: string,
    searchParams: { [key: string]: string }
): Required<RemoveUndefined<CommonSearchRequestType>> => ({
    shippingCountryCodeIso2: countryCodeIso2,
    languageCodeIso2: languageCodeIso2,
    automaticTranslationPreferred,
    categoryId: searchParams.categoryId
        ? parseInt(searchParams.categoryId)
        : null,
    ...getPriceRange(searchParams),
    itemStatus: searchParams.itemStatus,
    itemType: searchParams.itemType,
    itemCondition: searchParams.itemCondition,
    sortBy: searchParams.sortBy,
    paging: searchParams.paging,
    attributes: getAttributesWithoutPrefix(searchParams),
    fromNumberOfBids: searchParams.fromNumberOfBids
        ? parseInt(searchParams.fromNumberOfBids)
        : null,
    toNumberOfBids: searchParams.toNumberOfBids
        ? parseInt(searchParams.toNumberOfBids)
        : null,
    pageSize: searchParams.pageSize ? parseInt(searchParams.pageSize) : null
});
